import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue'
import type { GridApi, CellValueChangedEvent, CellEditingStoppedEvent } from 'ag-grid-community'
import { useDebounceFn } from '@vueuse/core'
import { extractGenus, generatePlantCode, parseCellValue, shouldCallExternalApis } from './usePlantUtils'

// Types for the cell response system
export interface CellResponseRule {
  /** Source field that triggers the response */
  sourceField: string
  /** Target fields that will be updated */
  targetFields: string[]
  /** Function to transform source value into target values */
  transform: (sourceValue: any, sourceRow: any, context: CellResponseContext) => Promise<Record<string, any>> | Record<string, any>
  /** Debounce delay in milliseconds (default: 300) */
  debounceMs?: number
  /** Whether to emit events for the updates */
  emitEvents?: boolean
  /** Custom event names for each target field */
  eventNames?: Record<string, string>
  /** Description for debugging */
  description?: string
  /** Conditions under which this rule should apply */
  condition?: (sourceValue: any, sourceRow: any, context: CellResponseContext) => boolean
  /** Priority level (lower numbers processed first) */
  priority?: number
  /** Whether this transformer uses external APIs */
  usesExternalApi?: boolean
}

export interface CellResponseContext {
  /** Current grid API instance */
  gridApi?: GridApi
  /** Row ID for accurate targeting */
  rowId?: string
  /** Source of the change (user-edit, api-response, etc.) */
  source?: string
  /** Whether async operations are disabled */
  asyncDisabled?: boolean
  /** Source chain from previous transformations */
  sourceChain?: Array<{
    field: string
    value: any
    timestamp: number
    trigger: 'user-edit' | 'api-response' | 'transformation' | 'autocomplete-selection'
  }>
}

export interface QueuedUpdate {
  /** Row identifier for accurate targeting */
  rowId?: string
  /** Source information for debugging */
  source: {
    field: string
    value: any
    timestamp: number
  }
  /** Source chain array tracking the sequence of events */
  sourceChain: Array<{
    field: string
    value: any
    timestamp: number
    trigger: 'user-edit' | 'api-response' | 'transformation' | 'autocomplete-selection'
  }>
  /** Updates to apply */
  updates: Record<string, any>
  /** Events to emit */
  events?: Array<{
    name: string
    data: any
  }>
  /** Priority (lower numbers processed first) */
  priority?: number
}

export interface CellResponseOptions {
  /** Global debounce for update processing (default: 100ms) */
  processingDebounceMs?: number
  /** Maximum queue size before dropping old updates */
  maxQueueSize?: number
  /** Whether to enable detailed logging */
  enableLogging?: boolean
  /** Custom emitter for events */
  emitter?: any
}

/**
 * Generic cell response system for AG Grid
 * Handles debounced API calls and queued updates during cell editing
 */
export function useCellResponseSystem(options: CellResponseOptions = {}) {
  const {
    processingDebounceMs = 50, // Reduced from 100ms to 50ms for faster processing
    maxQueueSize = 1000,
    enableLogging = false,
    emitter
  } = options

  // State management
  const rules = ref<CellResponseRule[]>([])
  const updateQueue = ref<QueuedUpdate[]>([])
  const isProcessingQueue = ref(false)
  const isActiveEditing = ref(false)
  const asyncOperationsDisabled = ref(false)
  
  // Grid API reference
  const gridApi = ref<GridApi>()

  // Logging utility - sanitize data to prevent Vue compilation warnings
  const log = (message: string, data?: any) => {
    if (enableLogging) {
      if (data && typeof data === 'object') {
        // Convert complex objects to safe JSON strings to prevent Vue warnings
        try {
          const safeData = JSON.parse(JSON.stringify(data))
          console.log(`[CellResponseSystem] ${message}`, safeData)
        } catch (error) {
          // If JSON serialization fails, just log a simple description
          console.log(`[CellResponseSystem] ${message}`, '[Complex Object]')
        }
      } else {
        console.log(`[CellResponseSystem] ${message}`, data || '')
      }
    }
  }

  // Register a new response rule
  const registerRule = (rule: CellResponseRule) => {
    log(`Registering rule: ${rule.sourceField} -> [${rule.targetFields.join(', ')}]`, rule.description)
    rules.value.push(rule)
  }

  // Remove a rule
  const unregisterRule = (sourceField: string, targetFields?: string[]) => {
    const initialLength = rules.value.length
    rules.value = rules.value.filter(rule => {
      if (rule.sourceField !== sourceField) return true
      if (!targetFields) return false
      return !targetFields.every(field => rule.targetFields.includes(field))
    })
    log(`Removed ${initialLength - rules.value.length} rules for ${sourceField}`)
  }

  // Get applicable rules for a field
  const getRulesForField = (field: string): CellResponseRule[] => {
    return rules.value.filter(rule => rule.sourceField === field)
  }

  // Helper function to check if commonname updates should be cancelled
  const shouldCancelCommonnameUpdate = (
    rule: CellResponseRule, 
    sourceRow: any, 
    sourceChain: Array<{field: string, value: any, timestamp: number, trigger: string}>
  ): boolean => {
    // Only check for commonname source rules
    if (rule.sourceField !== 'commonname') return false
    
    // Check if the original trigger in the source chain was commonname
    const originalTrigger = sourceChain[0]
    if (originalTrigger?.field !== 'commonname') return false
    
    // Check if botanical name already exists and has a value
    const botanicalName = sourceRow?.botanicalname
    if (botanicalName) {
      const botanicalValue = typeof botanicalName === 'object' 
        ? (botanicalName.value || botanicalName.label) 
        : botanicalName
      
      if (botanicalValue && botanicalValue.trim() !== '') {
        log(`Cancelling commonname-triggered update because botanical name already exists: ${botanicalValue}`)
        return true
      }
    }
    
    return false
  }

  // Create debounced transformers for each unique debounce time
  const debouncedTransformers = new Map<number, ReturnType<typeof useDebounceFn>>()

  const getOrCreateDebouncedTransformer = (debounceMs: number) => {
    if (!debouncedTransformers.has(debounceMs)) {
      debouncedTransformers.set(debounceMs, useDebounceFn(async (
        rule: CellResponseRule,
        sourceValue: any,
        sourceRow: any,
        context: CellResponseContext
      ) => {
        await processRule(rule, sourceValue, sourceRow, context)
      }, debounceMs))
    }
    return debouncedTransformers.get(debounceMs)!
  }

  // Process a single rule
  const processRule = async (
    rule: CellResponseRule,
    sourceValue: any,
    sourceRow: any,
    context: CellResponseContext
  ) => {
    try {
      log(`Processing rule: ${rule.sourceField} -> [${rule.targetFields.join(', ')}] (${rule.description})`)

      // Build source chain for this update
      const sourceChain = context.sourceChain || []
      const currentChainEntry = {
        field: rule.sourceField,
        value: sourceValue,
        timestamp: Date.now(),
        trigger: context.source === 'user-edit' ? 'user-edit' as const : 'transformation' as const
      }
      const updatedSourceChain = [...sourceChain, currentChainEntry]

      // Check if this commonname update should be cancelled
      if (shouldCancelCommonnameUpdate(rule, sourceRow, updatedSourceChain)) {
        log(`Skipping rule ${rule.sourceField} -> [${rule.targetFields.join(', ')}] due to existing botanical name`)
        return
      }

      // Check condition if provided
      if (rule.condition && !rule.condition(sourceValue, sourceRow, context)) {
        log(`Rule condition not met for ${rule.sourceField}`)
        return
      }

      // Apply transformation
      const updates = await rule.transform(sourceValue, sourceRow, context)
      
      if (!updates || Object.keys(updates).length === 0) {
        log(`No updates generated for ${rule.sourceField}`)
        return
      }

      // Create events if needed
      const events: Array<{ name: string, data: any }> = []
      if (rule.emitEvents) {
        rule.targetFields.forEach(targetField => {
          if (updates[targetField] !== undefined) {
            const eventName = rule.eventNames?.[targetField] || `grid:cell:update:${targetField}`
            events.push({
              name: eventName,
              data: {
                [targetField]: updates[targetField],
                sourceField: rule.sourceField,
                sourceValue,
                rowId: context.rowId,
                timestamp: Date.now()
              }
            })
          }
        })
      }

      // Queue the update with source chain
      const queuedUpdate: QueuedUpdate = {
        rowId: context.rowId,
        source: {
          field: rule.sourceField,
          value: sourceValue,
          timestamp: Date.now()
        },
        sourceChain: updatedSourceChain,
        updates,
        events,
        priority: 1 // Default priority
      }

      // Store node reference for better targeting
      if ((context as any).nodeReference) {
        ;(queuedUpdate as any).nodeReference = (context as any).nodeReference
      }

      addToQueue(queuedUpdate)

    } catch (error) {
      console.error(`[CellResponseSystem] Error processing rule for ${rule.sourceField}:`, error)
    }
  }

  // Add update to queue with size management
  const addToQueue = (update: QueuedUpdate) => {
    // Remove old updates if queue is getting too large
    if (updateQueue.value.length >= maxQueueSize) {
      updateQueue.value = updateQueue.value.slice(-Math.floor(maxQueueSize * 0.8))
      log('Queue size limit reached, removed old updates')
    }

    updateQueue.value.push(update)
    log(`Added update to queue, size: ${updateQueue.value.length} [${update.source.field} -> ${Object.keys(update.updates).join(', ')}]`, 
        `Source chain: ${update.sourceChain.map(s => `${s.field}(${s.trigger})`).join(' -> ')}`)
  }

  // Debounced queue processor
  const processQueue = useDebounceFn(async () => {
    if (isProcessingQueue.value || isActiveEditing.value || asyncOperationsDisabled.value) {
          log(`Skipping queue processing (processing: ${isProcessingQueue.value}, editing: ${isActiveEditing.value}, asyncDisabled: ${asyncOperationsDisabled.value})`)
      return
    }

    if (updateQueue.value.length === 0) return

    isProcessingQueue.value = true
    log(`Starting queue processing, ${updateQueue.value.length} items`)

    try {
      // Sort by priority (lower numbers first)
      const sortedQueue = [...updateQueue.value].sort((a, b) => (a.priority || 1) - (b.priority || 1))
      
      for (const update of sortedQueue) {
        // Check if editing started during processing
        if (isActiveEditing.value) {
          log('Editing started during queue processing, pausing')
          break
        }

        await applyQueuedUpdate(update)
        
        // Remove the processed update from queue
        const index = updateQueue.value.findIndex(item => 
          item.source.timestamp === update.source.timestamp &&
          item.rowId === update.rowId
        )
        if (index >= 0) {
          updateQueue.value.splice(index, 1)
        }
      }

      log(`Queue processing completed, ${updateQueue.value.length} items remaining`)

    } catch (error) {
      console.error('[CellResponseSystem] Error processing queue:', error)
    } finally {
      isProcessingQueue.value = false
    }
  }, processingDebounceMs)

  // Apply a queued update to the grid (abbreviated for space)
  const applyQueuedUpdate = async (update: QueuedUpdate) => {
    if (!gridApi.value) {
      log('No grid API available for update application')
      return
    }

    log(`Applying queued update (rowId: ${update.rowId}, ${update.source.field} -> ${Object.keys(update.updates).join(', ')})`)
    log(`Source chain: ${update.sourceChain.map(s => `${s.field}(${s.trigger})`).join(' -> ')}`)

    // Find target row (simplified targeting logic)
    let targetNode: any = null
    
    if ((update as any).nodeReference) {
      targetNode = (update as any).nodeReference
    } else if (update.rowId) {
      gridApi.value.forEachNode(node => {
        if (node.data?._id === update.rowId) {
          targetNode = node
        }
      })
    }

    if (!targetNode) {
      log(`Could not find target row for update (rowId: ${update.rowId}). Skipping update.`)
      return
    }

    // Apply updates using AG Grid transaction system
    const updatedRowData = { ...targetNode.data }
    
    if (!updatedRowData._id && !updatedRowData.id) {
      updatedRowData._id = update.rowId || `row_${targetNode.rowIndex || Date.now()}`
    }
    
    Object.entries(update.updates).forEach(([field, value]) => {
      updatedRowData[field] = value
    })

    setTimeout(() => {
      if (gridApi.value) {
        try {
          const result = gridApi.value.applyTransaction({
            update: [updatedRowData]
          })
          
          if (result && result.update && result.update.length > 0) {
            log(`Transaction applied successfully: ${result.update.length} rows updated`)
          } else {
            // Fallback to setValue
            Object.entries(update.updates).forEach(([fieldName, value]) => {
              targetNode.setDataValue(fieldName, value)
            })
          }
        } catch (error) {
          console.error('[CellResponseSystem] Error applying transaction:', error)
        }
      }
    }, 0)

    // Emit events
    if (update.events && emitter) {
      update.events.forEach(event => {
        try {
          emitter.emit(event.name, event.data)
          log(`Emitted event: ${event.name}`)
        } catch (error) {
          console.error(`[CellResponseSystem] Error emitting event ${event.name}:`, error)
        }
      })
    }
  }

  // Main handler for cell value changes
  const handleCellValueChanged = async (event: CellValueChangedEvent) => {
    if (!event.colDef?.field || asyncOperationsDisabled.value) return

    const field = event.colDef.field
    const sourceValue = event.newValue
    const sourceRow = event.data
    
    // Create a more reliable row identifier
    const nodeId = (event as any).node?.id || event.rowIndex?.toString() || 'unknown'
    const rowId = event.data?._id || `node_${nodeId}_row_${event.rowIndex}`

    log(`Cell value changed: ${field} (rowId: ${rowId}, value: ${typeof sourceValue === 'object' ? '[object]' : sourceValue})`)

    const applicableRules = getRulesForField(field)
    if (applicableRules.length === 0) {
      log(`No rules found for field: ${field}`)
      return
    }

    // Initialize source chain for this user edit
    const initialSourceChain = [{
      field,
      value: sourceValue,
      timestamp: Date.now(),
      trigger: 'user-edit' as const
    }]

    const context: CellResponseContext = {
      gridApi: gridApi.value,
      rowId,
      source: 'user-edit',
      asyncDisabled: asyncOperationsDisabled.value,
      sourceChain: initialSourceChain
    }

    // Store node reference for better targeting
    ;(context as any).nodeReference = (event as any).node

    // Process each applicable rule with its own debounce
    for (const rule of applicableRules) {
      const debounceMs = rule.debounceMs || 300
      const debouncedProcessor = getOrCreateDebouncedTransformer(debounceMs)
      debouncedProcessor(rule, sourceValue, sourceRow, context)
    }
  }

  // Monitor editing state (simplified)
  const startEditingMonitor = () => {
    if (!gridApi.value) return

    const editingInterval = setInterval(() => {
      try {
        if (gridApi.value && typeof gridApi.value.getEditingCells === 'function') {
          const editingCells = gridApi.value.getEditingCells()
          const isEditing = Array.isArray(editingCells) && editingCells.length > 0
          const wasEditing = isActiveEditing.value
          isActiveEditing.value = isEditing

          if (wasEditing && !isEditing) {
            log('Editing stopped, processing queued updates')
            processQueue()
          }
        } else if (!gridApi.value) {
          clearInterval(editingInterval)
        }
      } catch (error) {
        console.error('[CellResponseSystem] Error in editing monitor:', error)
        clearInterval(editingInterval)
      }
    }, 50)

    onBeforeUnmount(() => {
      clearInterval(editingInterval)
    })
  }

  // Setup grid API
  const setupGrid = (api: GridApi) => {
    gridApi.value = api
    setTimeout(() => {
      if (gridApi.value && typeof gridApi.value.getEditingCells === 'function') {
        startEditingMonitor()
        log('Grid API set up, monitoring started')
      }
    }, 100)
  }

  // Other utility functions
  const setAsyncOperationsEnabled = (enabled: boolean) => {
    asyncOperationsDisabled.value = !enabled
    log(`Async operations ${enabled ? 'enabled' : 'disabled'}`)
    
    if (enabled && updateQueue.value.length > 0 && !isActiveEditing.value) {
      processQueue()
    }
  }

  const clearQueue = () => {
    const count = updateQueue.value.length
    updateQueue.value = []
    log(`Cleared ${count} items from queue`)
  }

  const queueStatus = computed(() => ({
    size: updateQueue.value.length,
    isProcessing: isProcessingQueue.value,
    isEditing: isActiveEditing.value,
    asyncDisabled: asyncOperationsDisabled.value
  }))

  // Watch for queue changes
  watch(() => updateQueue.value.length, () => {
    if (!isActiveEditing.value && !asyncOperationsDisabled.value) {
      processQueue()
    }
  })

  // Cleanup
  onBeforeUnmount(() => {
    debouncedTransformers.clear()
    clearQueue()
  })

  return {
    // Core functions
    registerRule,
    unregisterRule,
    setupGrid,
    
    // Event handlers  
    handleCellValueChanged,
    handleCellEditingStopped: (event: CellEditingStoppedEvent) => {
      log('Cell editing stopped, resuming queue processing if needed')
      if (updateQueue.value.length > 0 && !isProcessingQueue.value && !asyncOperationsDisabled.value) {
        processQueue()
      }
    },
    
    // Configuration
    setAsyncOperationsEnabled,
    
    // State
    queueStatus,
    
    // Utilities
    clearQueue,
    getRulesForField,
    
    // Internal state (for debugging)
    rules: computed(() => rules.value),
    updateQueue: computed(() => updateQueue.value)
  }
}

// ====================================================================
// BOTANICAL TRANSFORMATIONS (migrated from useTransformers_old.ts)
// ====================================================================

/**
 * Create a botanical transformations system with predefined rules
 */
export function useBotanicalCellResponseSystem(options: CellResponseOptions = {}) {
  const cellResponseSystem = useCellResponseSystem(options)
  
  // Register botanical transformation rules
  const registerBotanicalRules = () => {
    
    // Rule 1: Botanical name → Code (fast, internal transformation)
    cellResponseSystem.registerRule({
      sourceField: 'botanicalname',
      targetFields: ['code'],
      debounceMs: 0, // Fast response for internal transformations
      priority: 1, // High priority - process first
      usesExternalApi: false,
      description: 'Generate plant code from botanical name',
      condition: (sourceValue, sourceRow, context) => {
        const parsed = parseCellValue(sourceValue, 'botanicalname')
        const hasValidValue = parsed?.displayValue && parsed.displayValue.length > 2
        console.log(`[BotanicalTransforms-Code] Rule 1 condition check:`, {
          sourceValue,
          parsed,
          hasValidValue,
          displayValue: parsed?.displayValue
        })
        return hasValidValue
      },
      transform: (sourceValue, sourceRow, context) => {
        const parsed = parseCellValue(sourceValue, 'botanicalname')
        const botanicalName = parsed?.displayValue || ''
        
        console.log(`[BotanicalTransforms-Code] Generating code for: "${botanicalName}"`)
        
        if (!botanicalName) {
          console.log(`[BotanicalTransforms-Code] No botanical name found, skipping code generation`)
          return {}
        }
        
        const code = generatePlantCode(botanicalName)
        console.log(`[BotanicalTransforms-Code] Generated code: "${code}" for "${botanicalName}"`)
        
        return {
          code: {
            value: code,
            label: code,
            source: 'botanical-name-transform',
            botanicalName: botanicalName,
            updatedAt: new Date().toISOString()
          }
        }
      }
    })
    
    // Rule 2: Botanical name → Common name (using optimized GBIF API call)
    cellResponseSystem.registerRule({
      sourceField: 'botanicalname', 
      targetFields: ['commonname'],
      debounceMs: 500, // Longer debounce for API calls
      priority: 2, // Lower priority than internal transforms
      usesExternalApi: true,
      description: 'Fetch common name from GBIF API (single optimized call)',
      condition: (sourceValue, sourceRow, context) => {
        // Skip if common name already exists to prevent redundancy
        const existingCommonName = sourceRow?.commonname
        if (existingCommonName && (typeof existingCommonName === 'string' || existingCommonName?.value)) {
          console.log('[BotanicalTransforms] Skipping common name fetch - already exists')
          return false
        }
        return shouldCallExternalApis(sourceValue) && !context.asyncDisabled
      },
      transform: async (sourceValue, sourceRow, context) => {
        const parsed = parseCellValue(sourceValue, 'botanicalname')
        const botanicalName = parsed?.displayValue || ''
        
        if (!botanicalName) return {}
        
        try {
          console.log(`[BotanicalTransforms] Fetching common name for: ${botanicalName}`)
          
          // Use the simpler GBIF API call (without datasetKey) since it returns vernacular names
          const gbifResponse = await $fetch<{
            results: Array<{ 
              vernacularNames?: Array<{ vernacularName: string, language?: string }>
              scientificName?: string
              canonicalName?: string
            }>
          }>(`https://api.gbif.org/v1/species/search?q=${encodeURIComponent(botanicalName)}&rank=species&status=ACCEPTED&limit=5`, {
            timeout: 8000 // 8 second timeout
          })
          
          console.log('[BotanicalTransforms] GBIF response:', gbifResponse)
          
          // Add proper null checks for GBIF response
          if (!gbifResponse || typeof gbifResponse !== 'object' || !gbifResponse.results) {
            console.log('[BotanicalTransforms] Invalid GBIF response format')
            return {}
          }
          
          // Ensure results is an array before calling find
          if (!Array.isArray(gbifResponse.results) || gbifResponse.results.length === 0) {
            console.log('[BotanicalTransforms] No results found in GBIF response')
            return {}
          }
          
          console.log(`[BotanicalTransforms] Found ${gbifResponse.results.length} results, checking for vernacular names...`)
          
          // Find the first result that has vernacular names
          const result = gbifResponse.results.find((result) => {
            return result?.vernacularNames && 
                   Array.isArray(result.vernacularNames) && 
                   result.vernacularNames.length > 0
          })
          
          if (result && result.vernacularNames && result.vernacularNames.length > 0) {
            // Prefer English common names if available
            const englishName = result.vernacularNames.find(vn => 
              vn.language === 'eng' || vn.language === 'en'
            )
            const commonName = englishName?.vernacularName || result.vernacularNames[0].vernacularName
            
            console.log(`[BotanicalTransforms] Found common name: ${commonName}`)
            
            return {
              commonname: {
                value: commonName,
                label: commonName,
                source: 'gbif-optimized-api',
                botanicalName: botanicalName,
                updatedAt: new Date().toISOString()
              }
            }
          } else {
            console.log('[BotanicalTransforms] No vernacular names found in any results')
          }
        } catch (error) {
          console.warn('[BotanicalTransforms] Error fetching common name from GBIF API:', error)
        }
        
        return {}
      }
    })
    
    // Rule 3: Common name → Botanical name (with loop prevention)
    cellResponseSystem.registerRule({
      sourceField: 'commonname',
      targetFields: ['botanicalname'],
      debounceMs: 500,
      priority: 3,
      usesExternalApi: true,
      description: 'Lookup botanical name from common name (with loop prevention)',
      condition: (sourceValue, sourceRow, context) => {
        // Skip if botanical name already exists to prevent loops
        const existingBotanicalName = sourceRow?.botanicalname
        if (existingBotanicalName && (typeof existingBotanicalName === 'string' || existingBotanicalName?.value)) {
          console.log('[BotanicalTransforms] Skipping botanical name lookup - already exists')
          return false
        }

        // Also check source chain to prevent loops
        const sourceChain = context.sourceChain || []
        const hasBotanicalNameInChain = sourceChain.some(entry => entry.field === 'botanicalname')
        if (hasBotanicalNameInChain) {
          console.log('[BotanicalTransforms] Skipping botanical name lookup - botanical name in source chain (loop prevention)')
          return false
        }

        return shouldCallExternalApis(sourceValue) && !context.asyncDisabled
      },
      transform: async (sourceValue, sourceRow, context) => {
        const parsed = parseCellValue(sourceValue, 'commonname')
        const commonName = parsed?.displayValue || ''
        
        if (!commonName) return {}
        
        try {
          console.log(`[BotanicalTransforms] Looking up botanical name for common name: ${commonName}`)
          
          const response = await $fetch<{
            results: Array<{
              scientificName?: string
              canonicalName?: string
              vernacularNames?: Array<{ vernacularName: string }>
            }>
          }>(`https://api.gbif.org/v1/species/search?q=${encodeURIComponent(commonName)}&limit=10`, {
            timeout: 8000
          })
          
          if (response?.results?.length > 0) {
            // Find a result that has matching vernacular names
            const matchingResult = response.results.find(result => 
              result.vernacularNames?.some(vn => 
                vn.vernacularName.toLowerCase().includes(commonName.toLowerCase())
              )
            )
            
            const botanicalName = matchingResult?.canonicalName || matchingResult?.scientificName
            
            if (botanicalName) {
              console.log(`[BotanicalTransforms] Found botanical name for '${commonName}': ${botanicalName}`)
              return {
                botanicalname: {
                  label: botanicalName,
                  value: botanicalName,
                  source: 'gbif-reverse-lookup',
                  commonName: commonName,
                  updatedAt: new Date().toISOString()
                }
              }
            }
          }
          
          return {}
        } catch (error) {
          console.warn('[BotanicalTransforms] Error looking up botanical name:', error)
          return {}
        }
      }
    })

    // Rule 4: Botanical name → matureheight + maturewidth (GIFT API first, Perplexity fallback)
    cellResponseSystem.registerRule({
      sourceField: 'botanicalname',
      targetFields: ['matureheight', 'maturewidth'],
      debounceMs: 600, // Longer debounce for multi-step API calls
      priority: 4, // Lower priority to allow other transforms first
      usesExternalApi: true,
      description: 'Fetch plant dimensions using GIFT API (search.post.ts) first, then fallback to Perplexity',
      condition: (sourceValue, sourceRow, context) => {
        return shouldCallExternalApis(sourceValue) && !context.asyncDisabled
      },
      transform: async (sourceValue, sourceRow, context) => {
        const parsed = parseCellValue(sourceValue, 'botanicalname')
        const botanicalName = parsed?.displayValue || ''
        
        if (!botanicalName) return {}
        
        try {
          console.log(`[BotanicalTransforms] Fetching plant dimensions for: ${botanicalName}`)
          
          // STEP 1: Try GIFT API first (via search.post.ts endpoint)
          try {
            console.log(`[BotanicalTransforms] Trying GIFT API via fetchPlantTraits for: ${botanicalName}`)
            
            // Import and use the fetchPlantTraits function from useValidation
            const { useValidation } = await import('./useValidation')
            const { fetchPlantTraits } = useValidation()
            
            const giftTraits = await fetchPlantTraits(botanicalName)
            
            if (giftTraits && (giftTraits.matureheight || giftTraits.maturewidth)) {
              console.log(`[BotanicalTransforms] GIFT API returned traits:`, giftTraits)
              
              const updates: Record<string, any> = {}
              
              if (giftTraits.matureheight) {
                updates.matureheight = {
                  ...giftTraits.matureheight,
                  source: 'gift-api-search',
                  updatedAt: new Date().toISOString()
                }
              }
              
              if (giftTraits.maturewidth) {
                updates.maturewidth = {
                  ...giftTraits.maturewidth,
                  source: 'gift-api-search',
                  updatedAt: new Date().toISOString()
                }
              }
              
              console.log(`[BotanicalTransforms] Returning GIFT API results:`, updates)
              return updates
            } else {
              console.log(`[BotanicalTransforms] GIFT API returned no useful traits, trying Perplexity fallback`)
            }
          } catch (giftError) {
            console.warn(`[BotanicalTransforms] GIFT API failed for ${botanicalName}:`, giftError)
            console.log(`[BotanicalTransforms] Proceeding to Perplexity fallback`)
          }
          
          // STEP 2: Fallback to Perplexity API if GIFT API failed or returned no data
          try {
            console.log(`[BotanicalTransforms] Trying Perplexity API fallback for: ${botanicalName}`)
            
            const perplexityPrompt = `What are the mature height and width (canopy spread) of ${botanicalName}? 
            
Please provide specific measurements in meters if possible. Format your response clearly with:
- Mature Height: [value] meters
- Mature Width/Spread: [value] meters

Include your sources and be as specific as possible with the measurements.`

            const perplexityResponse = await $fetch<{
              text: string
              citations?: any[]
              _fullApiResponse?: any
            }>('/api/ai/pplx', {
              method: 'POST',
              body: {
                prompt: perplexityPrompt,
                temperature: 0.2,
                topP: 0.9,
                return_citations: true
              },
              timeout: 12000 // 12 second timeout for Perplexity
            })
            
            if (perplexityResponse?.text) {
              console.log(`[BotanicalTransforms] Perplexity response:`, perplexityResponse.text)
              
              const updates: Record<string, any> = {}
              const responseText = perplexityResponse.text.toLowerCase()
              
              // Try to extract height from response
              const heightMatch = responseText.match(/(?:mature\s+)?height[:\s]*(\d+(?:\.\d+)?)\s*(?:meters?|m\b)/i)
              if (heightMatch) {
                const heightValue = parseFloat(heightMatch[1])
                if (!isNaN(heightValue) && heightValue > 0) {
                  updates.matureheight = {
                    value: heightValue,
                    source: 'perplexity-api-fallback',
                    sourceText: perplexityResponse.text,
                    citations: perplexityResponse.citations,
                    updatedAt: new Date().toISOString()
                  }
                  console.log(`[BotanicalTransforms] Extracted height from Perplexity: ${heightValue}m`)
                }
              }
              
              // Try to extract width/spread from response
              const widthMatch = responseText.match(/(?:mature\s+)?(?:width|spread|canopy)[:\s]*(\d+(?:\.\d+)?)\s*(?:meters?|m\b)/i)
              if (widthMatch) {
                const widthValue = parseFloat(widthMatch[1])
                if (!isNaN(widthValue) && widthValue > 0) {
                  updates.maturewidth = {
                    value: widthValue,
                    source: 'perplexity-api-fallback',
                    sourceText: perplexityResponse.text,
                    citations: perplexityResponse.citations,
                    updatedAt: new Date().toISOString()
                  }
                  console.log(`[BotanicalTransforms] Extracted width from Perplexity: ${widthValue}m`)
                }
              }
              
              if (Object.keys(updates).length > 0) {
                console.log(`[BotanicalTransforms] Returning Perplexity fallback results:`, updates)
                return updates
              } else {
                console.log(`[BotanicalTransforms] Could not extract dimensions from Perplexity response`)
              }
            }
          } catch (perplexityError) {
            console.warn(`[BotanicalTransforms] Perplexity API also failed for ${botanicalName}:`, perplexityError)
          }
          
          console.log(`[BotanicalTransforms] Both GIFT and Perplexity APIs failed for ${botanicalName}`)
          return {}
          
        } catch (error) {
          console.error(`[BotanicalTransforms] Error fetching plant dimensions for ${botanicalName}:`, error)
          return {}
        }
      }
    })

    // Rule 5: Dedicated maturewidth-only responder (based on old implementation)
    cellResponseSystem.registerRule({
      sourceField: 'botanicalname',
      targetFields: ['maturewidth'],
      debounceMs: 800, // Even longer debounce for sophisticated multi-step process
      priority: 5, // Lower priority to allow other transforms first
      usesExternalApi: true,
      description: 'Dedicated maturewidth lookup with cache check, Gemini primary, Perplexity fallback (skipped if Rule 4 succeeded)',
      condition: (sourceValue, sourceRow, context) => {
        // Skip if maturewidth already exists (likely from Rule 4)
        const existingWidth = sourceRow?.maturewidth
        if (existingWidth && (typeof existingWidth === 'object' && existingWidth?.value)) {
          console.log('[BotanicalTransforms-Width] Skipping dedicated width lookup - width already exists from Rule 4')
          return false
        }

        return shouldCallExternalApis(sourceValue) && !context.asyncDisabled
      },
      transform: async (sourceValue, sourceRow, context) => {
        const parsed = parseCellValue(sourceValue, 'botanicalname')
        const botanicalName = parsed?.displayValue || ''
        
        if (!botanicalName) return {}
        
        try {
          console.log(`[BotanicalTransforms-Width] Dedicated maturewidth lookup for: ${botanicalName}`)
          
          // STEP 1: Check Supabase cache first (if available)
          let cachedResult = null
          try {
            // Only try cache if Supabase client is available
            const { useSupabaseClient } = await import('#imports')
            const supabaseClient = useSupabaseClient()
            
            if (supabaseClient) {
              console.log(`[BotanicalTransforms-Width] Checking cache for: ${botanicalName}`)
              
              // First check for any cached data with actual width values
              const { data: cachedDataWithWidth } = await supabaseClient
                .from('botanicaldata_cache')
                .select('*')
                .eq('field', 'maturewidth')
                .eq('speciesname', botanicalName)
                .not('results->width', 'is', null)
                .maybeSingle() as { data: any }

              if (cachedDataWithWidth?.results) {
                console.log(`[BotanicalTransforms-Width] Found cached width data:`, cachedDataWithWidth)
                
                const cachedResults = cachedDataWithWidth.results
                const widthArray = cachedResults.width_array || [cachedResults.width]
                
                // Get source information
                let sourceExcerpts = []
                if (cachedResults.rawJsonResponse?.supporting_excerpt) {
                  sourceExcerpts = cachedResults.rawJsonResponse.supporting_excerpt
                } else if (cachedResults.groundingMetadata?.sourceExcerpts) {
                  sourceExcerpts = cachedResults.groundingMetadata.sourceExcerpts
                }
                
                // Get source URLs
                let sourceUrls = []
                if (cachedResults.rawJsonResponse?.groundingMetadata) {
                  sourceUrls = cachedResults.rawJsonResponse.groundingMetadata
                } else if (cachedResults.groundingMetadata?.sourceUrls) {
                  sourceUrls = cachedResults.groundingMetadata.sourceUrls
                }
                
                return {
                  maturewidth: {
                    value: cachedResults.width,
                    units: cachedResults.units || 'm',
                    source: 'cached-supabase',
                    groundingMetadata: {
                      confidence: cachedResults.confidence || cachedResults.groundingMetadata?.confidence || null,
                      sourceText: cachedResults.sourceText || cachedResults.groundingMetadata?.sourceText || null,
                      sourceUrls: sourceUrls,
                      sourceExcerpts: sourceExcerpts,
                      searchQueries: cachedResults.groundingMetadata?.searchQueries || []
                    },
                    sourceUrl: cachedResults.sourceUrl || (sourceUrls.length > 0 ? sourceUrls[0] : null),
                    sourceName: cachedResults.sourceName || (sourceExcerpts.length > 0 ? sourceExcerpts[0] : "Cached data"),
                    sourceText: cachedResults.sourceText || '',
                    confidence: cachedResults.confidence || cachedResults.groundingMetadata?.confidence || null,
                    widthArray: widthArray,
                    createdAt: cachedDataWithWidth.created_at,
                    updatedAt: new Date().toISOString()
                  }
                }
              }
            }
          } catch (cacheError) {
            console.warn(`[BotanicalTransforms-Width] Cache check failed for ${botanicalName}:`, cacheError)
          }
          
          // STEP 2: Try Gemini API as primary source using hidden prompt (better references via Google grounded search)
          let geminiResult = null
          try {
            console.log(`[BotanicalTransforms-Width] Trying Gemini API (primary) for: ${botanicalName}`)
            
            const geminiResponse = await $fetch<{
              text?: string
              candidates?: Array<{ content?: { parts?: Array<{ text?: string }> } }>
              _groundingMetadata?: any
              width?: number[]
              units?: string
              groundingMetadata?: any[]
              supporting_excerpt?: string[]
            }>('/api/ai/gmni-gnrd', {
              method: 'POST',
              body: {
                prompts: [
                  {
                    hidden: true,
                    prompt: 'matureWidthPrompt'
                  },
                  {
                    hidden: false,
                    prompt: botanicalName
                  }
                ],
                responseSchema: {
                  type: "object",
                  properties: {
                    width: {
                      type: "array",
                      items: { type: "number" },
                      description: "Array of numeric width values in meters"
                    },
                    units: {
                      type: "string",
                      description: "Always 'm' for meters"
                    },
                    groundingMetadata: {
                      type: "array",
                      items: { type: "string" },
                      description: "Array of source URLs"
                    },
                    supporting_excerpt: {
                      type: "array",
                      items: { type: "string" },
                      description: "Array of source titles or excerpts"
                    }
                  },
                  required: ["width", "units"]
                },
                temperature: 0.15,
                topK: 40,
                topP: 0.95
              },
              timeout: 10000
            })
            
            if (geminiResponse) {
              console.log(`[BotanicalTransforms-Width] Gemini response:`, geminiResponse)
              
              // Try to get structured data first (if responseSchema worked)
              if (geminiResponse.width && Array.isArray(geminiResponse.width)) {
                const widthValues = geminiResponse.width
                  .filter(val => !isNaN(val) && val > 0 && val < 100)
                
                if (widthValues.length > 0) {
                  const widthValue = widthValues[0]
                  
                  geminiResult = {
                    maturewidth: {
                      value: widthValue,
                      units: geminiResponse.units || 'm',
                      source: 'gemini-structured-primary',
                      groundingMetadata: geminiResponse._groundingMetadata || geminiResponse.groundingMetadata || [],
                      supporting_excerpt: geminiResponse.supporting_excerpt || [],
                      widthArray: widthValues.slice(0, 5),
                      confidence: 'high',
                      updatedAt: new Date().toISOString()
                    }
                  }
                  
                  console.log(`[BotanicalTransforms-Width] Gemini structured response: ${widthValue}m (${widthValues.length} values)`)
                }
              }
              
              // Fallback to text extraction from Gemini response if structured didn't work
              if (!geminiResult) {
                let geminiText = ''
                if (geminiResponse.text) {
                  geminiText = geminiResponse.text
                } else if (geminiResponse.candidates?.[0]?.content?.parts?.[0]?.text) {
                  geminiText = geminiResponse.candidates[0].content.parts[0].text
                }
                
                if (geminiText) {
                  // First try to parse as JSON from text
                  try {
                    const jsonMatch = geminiText.match(/```json\n([\s\S]*?)\n```/) || geminiText.match(/\{[\s\S]*\}/)
                    if (jsonMatch) {
                      const jsonContent = jsonMatch[1] || jsonMatch[0]
                      const parsedResponse = JSON.parse(jsonContent)
                      
                      if (parsedResponse.width && Array.isArray(parsedResponse.width)) {
                        const widthValues = parsedResponse.width
                          .filter((val: any) => !isNaN(parseFloat(val)) && parseFloat(val) > 0 && parseFloat(val) < 100)
                        
                        if (widthValues.length > 0) {
                          const widthValue = parseFloat(widthValues[0])
                          
                          geminiResult = {
                            maturewidth: {
                              value: widthValue,
                              units: parsedResponse.units || 'm',
                              source: 'gemini-json-primary',
                              sourceText: geminiText,
                              groundingMetadata: parsedResponse.groundingMetadata || geminiResponse._groundingMetadata || [],
                              supporting_excerpt: parsedResponse.supporting_excerpt || [],
                              widthArray: widthValues.slice(0, 5).map((v: any) => parseFloat(v)),
                              confidence: 'medium',
                              updatedAt: new Date().toISOString()
                            }
                          }
                          
                          console.log(`[BotanicalTransforms-Width] Gemini JSON response: ${widthValue}m (${widthValues.length} values)`)
                        }
                      }
                    }
                  } catch (jsonParseError) {
                    console.log(`[BotanicalTransforms-Width] Failed to parse Gemini JSON, trying text extraction`)
                  }
                  
                  // Final regex extraction for Gemini
                  if (!geminiResult) {
                    const responseText = geminiText.toLowerCase()
                    const widthMatches = [
                      ...responseText.matchAll(/(?:width|spread|canopy)[:\s]*(\d+(?:\.\d+)?)\s*(?:meters?|m\b)/gi),
                      ...responseText.matchAll(/(\d+(?:\.\d+)?)\s*(?:meters?|m\b).*(?:width|spread|canopy)/gi)
                    ]
                    
                    if (widthMatches.length > 0) {
                      const widthValues = widthMatches
                        .map(match => parseFloat(match[1]))
                        .filter(val => !isNaN(val) && val > 0 && val < 100)
                      
                      if (widthValues.length > 0) {
                        const widthValue = widthValues[0]
                        
                        geminiResult = {
                          maturewidth: {
                            value: widthValue,
                            units: 'm',
                            source: 'gemini-text-primary',
                            sourceText: geminiText,
                            groundingMetadata: geminiResponse._groundingMetadata || {},
                            widthArray: widthValues.slice(0, 5),
                            confidence: 'medium',
                            updatedAt: new Date().toISOString()
                          }
                        }
                        
                        console.log(`[BotanicalTransforms-Width] Gemini text extraction: ${widthValue}m (${widthValues.length} values found)`)
                      }
                    }
                  }
                }
              }
            }
          } catch (geminiError) {
            console.warn(`[BotanicalTransforms-Width] Gemini primary failed for ${botanicalName}:`, geminiError)
          }
          
          // STEP 3: Try Perplexity API as fallback if Gemini failed
          if (!geminiResult) {
            try {
              console.log(`[BotanicalTransforms-Width] Trying Perplexity API fallback for: ${botanicalName}`)
              
              const perplexityResponse = await $fetch<{
                text: string
                citations?: any[]
                _fullApiResponse?: any
              }>('/api/ai/pplx', {
                method: 'POST',
                body: {
                  prompts: [
                    {
                      hidden: true,
                      prompt: 'matureWidthPrompt'
                    },
                    {
                      hidden: false,
                      prompt: botanicalName
                    }
                  ],
                  temperature: 0.2,
                  topP: 0.9,
                  return_citations: true
                },
                timeout: 12000
              })
              
              if (perplexityResponse?.text) {
                console.log(`[BotanicalTransforms-Width] Perplexity response:`, perplexityResponse.text)
                
                // First try to parse as JSON (expected from matureWidthPrompt)
                try {
                  const jsonResponse = JSON.parse(perplexityResponse.text)
                  
                  if (jsonResponse.width && Array.isArray(jsonResponse.width)) {
                    const widthValues = jsonResponse.width
                      .filter((val: any) => !isNaN(parseFloat(val)) && parseFloat(val) > 0 && parseFloat(val) < 100)
                    
                    if (widthValues.length > 0) {
                      const widthValue = parseFloat(widthValues[0])
                      
                      return {
                        maturewidth: {
                          value: widthValue,
                          units: jsonResponse.units || 'm',
                          source: 'perplexity-structured-fallback',
                          sourceText: perplexityResponse.text,
                          citations: perplexityResponse.citations || [],
                          widthArray: widthValues.slice(0, 5).map((v: any) => parseFloat(v)),
                          groundingMetadata: jsonResponse.groundingMetadata || [],
                          supporting_excerpt: jsonResponse.supporting_excerpt || [],
                          confidence: widthValues.length > 1 ? 'medium' : 'low',
                          updatedAt: new Date().toISOString()
                        }
                      }
                    }
                  }
                } catch (parseError) {
                  console.log(`[BotanicalTransforms-Width] Failed to parse Perplexity JSON, trying text extraction`)
                  
                  // Fallback to regex extraction if JSON parsing fails
                  const responseText = perplexityResponse.text.toLowerCase()
                  const widthMatches = [
                    ...responseText.matchAll(/(?:width|spread|canopy)[:\s]*(\d+(?:\.\d+)?)\s*(?:meters?|m\b)/gi),
                    ...responseText.matchAll(/(\d+(?:\.\d+)?)\s*(?:meters?|m\b).*(?:width|spread|canopy)/gi),
                    ...responseText.matchAll(/(\d+(?:\.\d+)?)\s*m\b/gi)
                  ]
                  
                  if (widthMatches.length > 0) {
                    const widthValues = widthMatches
                      .map(match => parseFloat(match[1]))
                      .filter(val => !isNaN(val) && val > 0 && val < 100)
                    
                    if (widthValues.length > 0) {
                      const widthValue = widthValues[0]
                      
                      return {
                        maturewidth: {
                          value: widthValue,
                          units: 'm',
                          source: 'perplexity-extracted-fallback',
                          sourceText: perplexityResponse.text,
                          citations: perplexityResponse.citations || [],
                          widthArray: widthValues.slice(0, 5),
                          confidence: widthValues.length > 1 ? 'medium' : 'low',
                          updatedAt: new Date().toISOString()
                        }
                      }
                    }
                  }
                }
              }
            } catch (perplexityError) {
              console.warn(`[BotanicalTransforms-Width] Perplexity fallback also failed for ${botanicalName}:`, perplexityError)
            }
          }
          
          // Return Gemini result if we found one
          if (geminiResult) {
            console.log(`[BotanicalTransforms-Width] Returning Gemini result:`, geminiResult)
            return geminiResult
          }
          
          console.log(`[BotanicalTransforms-Width] No width data found for ${botanicalName}`)
          return {}
          
        } catch (error) {
          console.error(`[BotanicalTransforms-Width] Error in dedicated width lookup for ${botanicalName}:`, error)
          return {}
        }
      }
    })
  }
  
  // Auto-register botanical rules
  registerBotanicalRules()
  console.log('[BotanicalCellResponseSystem] Registered botanical rules:', cellResponseSystem.rules.value.map(r => `${r.sourceField} -> [${r.targetFields.join(', ')}]`))
  
  return {
    ...cellResponseSystem,
    // Additional botanical-specific utilities
    extractGenus,
    generatePlantCode,
    parseCellValue,
    registerBotanicalRules,
    // Create custom rules easily
    createBotanicalRule: (config: Omit<CellResponseRule, 'sourceField'> & { sourceField?: string }) => {
      const rule: CellResponseRule = {
        sourceField: 'botanicalname',
        ...config
      }
      cellResponseSystem.registerRule(rule)
      return rule
    }
  }
} 